import React, { useState, useCallback, useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { Search, Loader2 } from "../ui/icons";
import { ApiForwardGeocoding } from "../../services/api";
import { GeocodingFeature } from "../../global/types/geocoding";
import { Input } from "../ui/input";
import { cn } from "../../lib/utils";
import { GeocodingSuggestions } from "./GeocodingSuggestions";
import {
  normalizeLatLng,
  isInsidePernambuco,
  PE_LAT,
  PE_LNG,
} from "../../utils/geo";

interface GeocodingSearchBarProps {
  table?: string;
  limit?: number;
  className?: string;
}

const COORD_REGEX = /^(-?\d+(\.\d+)?)\s*,\s*(-?\d+(\.\d+)?)$/;

function applyCoordMask(raw: string): string {
  let cleaned = raw.replace(/[^\d\-.,\s]/g, "");
  const commaIndex = cleaned.indexOf(",");
  if (commaIndex !== -1) {
    const before = cleaned.slice(0, commaIndex + 1);
    const after = cleaned.slice(commaIndex + 1).replace(/,/g, "");
    cleaned = before + after;
  }
  return cleaned;
}

function looksLikeCoord(value: string): boolean {
  return COORD_REGEX.test(value.trim());
}

function isTypingCoord(value: string): boolean {
  return /^-?[\d.]+\s*,/.test(value.trim());
}

export function GeocodingSearchBar({
  table = "municipios_pe",
  limit = 10,
  className,
}: GeocodingSearchBarProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<GeocodingFeature[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [coordHint, setCoordHint] = useState<string | null>(null);
  const [coordError, setCoordError] = useState<string | null>(null);

  const map = useMap();
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const executeZoomAndMarker = useCallback(
    (lat: number, lng: number, label?: string) => {
      if (markerRef.current) {
        map.removeLayer(markerRef.current);
      }

      map.flyTo([lat, lng], 14, { animate: true, duration: 1.5 });

      const displayLabel =
        label || `Coordenada: ${lat.toFixed(4)}, ${lng.toFixed(4)}`;

      const marker = L.marker([lat, lng]);
      const popup = L.popup({ closeButton: true, autoClose: false })
        .setLatLng([lat, lng])
        .setContent(
          `<div style="padding: 5px; font-weight: bold; color: #1e293b;">${displayLabel}</div>`
        );

      marker.bindPopup(popup).addTo(map).openPopup();
      markerRef.current = marker;

      setShowResults(false);
      setIsLoading(false);
    },
    [map]
  );

  const handleSearch = useCallback(
    async (searchQuery: string) => {
      if (!searchQuery || searchQuery.length < 2) {
        setResults([]);
        setShowResults(false);
        setCoordHint(null);
        setCoordError(null);
        return;
      }

      const match = searchQuery.match(COORD_REGEX);

      if (match) {
        const v1 = parseFloat(match[1]);
        const v2 = parseFloat(match[3]);

        if (Math.abs(v1) > 90 && Math.abs(v2) > 90) {
          setCoordError(
            "Coordenadas inválidas. Latitude deve estar entre -90 e 90."
          );
          setCoordHint(null);
          return;
        }

        const { lat, lng } = normalizeLatLng(v1, v2);

        if (!isInsidePernambuco(lat, lng)) {
          setCoordError(
            `Coordenada fora de Pernambuco. Use: Lat entre ${PE_LAT.min} e ${PE_LAT.max}, Lon entre ${PE_LNG.min} e ${PE_LNG.max}.`
          );
          setCoordHint(null);
          return;
        }

        setCoordError(null);

        const wasInverted = lat === v2 && lng === v1;
        if (wasInverted) {
          setCoordHint(
            `Ordem invertida detectada. Ajustado para Lat: ${lat.toFixed(
              4
            )}, Lon: ${lng.toFixed(4)}`
          );
        } else {
          setCoordHint(null);
        }

        executeZoomAndMarker(lat, lng);
        return;
      }

      if (isTypingCoord(searchQuery)) {
        setCoordHint(null);
        setCoordError(null);
        return;
      }

      setCoordHint(null);
      setCoordError(null);
      setIsLoading(true);
      setHasError(false);
      try {
        const response = await ApiForwardGeocoding({
          query: searchQuery,
          table,
          limit,
        });
        setResults(response || []);
        setShowResults(true);
      } catch (error) {
        console.error("Geocoding search error:", error);
        setResults([]);
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [table, limit, executeZoomAndMarker]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    if (isTypingCoord(value) || looksLikeCoord(value)) {
      value = applyCoordMask(value);
    }

    setQuery(value);
    setHasError(false);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => handleSearch(value), 500);
  };

  const handleSelectFeature = (feature: GeocodingFeature) => {
    const { lat, lon: lng } = feature.coordinates;
    const displayName =
      feature.properties?.nm_mun || feature.name || "Localização";
    setQuery(displayName);
    executeZoomAndMarker(lat, lng, displayName);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

      const match = query.match(COORD_REGEX);

      if (match) {
        const v1 = parseFloat(match[1]);
        const v2 = parseFloat(match[3]);
        const { lat, lng } = normalizeLatLng(v1, v2);

        if (!isInsidePernambuco(lat, lng)) {
          setCoordError(
            `Coordenada fora de Pernambuco. Use: Lat entre ${PE_LAT.min} e ${PE_LAT.max}, Lon entre ${PE_LNG.min} e ${PE_LNG.max}.`
          );
          return;
        }

        setCoordError(null);
        executeZoomAndMarker(lat, lng);
      } else if (results.length > 0) {
        handleSelectFeature(results[0]);
      } else if (query.length >= 2) {
        handleSearch(query);
      }
    }

    if (e.key === "Escape") {
      setQuery("");
      setResults([]);
      setShowResults(false);
      setCoordHint(null);
      setCoordError(null);
      if (markerRef.current) map.removeLayer(markerRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (markerRef.current) map.removeLayer(markerRef.current);
    };
  }, [map]);

  const dropdownState = coordError
    ? "coord_error"
    : coordHint
    ? "hint_coord"
    : query.length === 1
    ? "hint"
    : hasError
    ? "error"
    : showResults && !isLoading && results.length === 0 && query.length >= 2
    ? "empty"
    : showResults && results.length > 0
    ? "results"
    : null;

  return (
    <div
      className={cn(
        "fixed z-action-bar pointer-events-auto top-[75px] left-[10px] w-full max-w-[424px]",
        className
      )}
    >
      <div className="relative flex flex-col w-full bg-white rounded-xl shadow-md border border-slate-200 overflow-visible">
        <div className="flex items-center h-12 px-4 gap-2 relative">
          <div className="flex items-center justify-center shrink-0">
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
            ) : (
              <Search className="h-5 w-5 text-slate-400" />
            )}
          </div>

          <Input
            ref={inputRef}
            type="text"
            placeholder="Buscar localização ou coordenada (ex: -8.0631, -34.8711)"
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => results.length > 0 && setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 200)}
            className="flex-1 border-0 shadow-none focus-visible:ring-0 text-sm bg-transparent px-0 h-full text-slate-700"
          />

          {query.length > 0 && (
            <button
              onMouseDown={(e) => {
                e.preventDefault();
                setQuery("");
                setCoordHint(null);
                setCoordError(null);
              }}
              className="w-5 h-5 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center"
            >
              <span className="text-slate-500 text-[10px]">✕</span>
            </button>
          )}
        </div>

        {dropdownState && (
          <div
            className={cn(
              "absolute top-[calc(100%+6px)] left-0 w-full bg-white rounded-xl shadow-lg border border-slate-100 z-[1005]",
              dropdownState === "results" ? "overflow-hidden" : "px-4 py-3"
            )}
          >
            {dropdownState === "coord_error" && (
              <div className="flex items-start gap-2">
                <span className="text-red-500 mt-0.5 shrink-0">⚠</span>
                <p className="text-sm text-red-600 font-medium">{coordError}</p>
              </div>
            )}
            {dropdownState === "hint_coord" && (
              <div className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5 shrink-0">↕</span>
                <p className="text-sm text-amber-600 font-medium">
                  {coordHint}
                </p>
              </div>
            )}
            {dropdownState === "hint" && (
              <p className="text-sm text-slate-400">
                Digite pelo menos 2 caracteres ou uma coordenada (ex: -8.06,
                -34.87)
              </p>
            )}
            {dropdownState === "error" && (
              <p className="text-sm text-red-500">
                Erro ao buscar localização. Tente novamente.
              </p>
            )}
            {dropdownState === "empty" && (
              <p className="text-sm text-slate-500">
                Nenhuma cidade encontrada para{" "}
                <span className="font-medium text-slate-700">"{query}"</span>
              </p>
            )}
            {dropdownState === "results" && (
              <GeocodingSuggestions
                results={results}
                onSelect={handleSelectFeature}
                maxResults={limit}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
